import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Router from 'next/router';

import { getLogger } from 'src/utils/datadog-logger';
import { BAR_TEST_ID, BUTTON_TEST_IDS, DIAGNOSTICS_LOGGER_NAMESPACE } from './constants';
import { collectEnvSnapshot } from './env-collector';
import { computeHitMap, describeElement, getRectSnapshot, probeBarHitRange, round } from './utils';
import type {
  DiagnosticsFlagConfig,
  ElementDescriptor,
  HudState,
  RectSnapshot,
  TouchEventLogPayload,
  TouchSnapshot,
} from './types';

const INPUT_EVENT_TYPES = [
  'pointerdown',
  'pointerup',
  'pointercancel',
  'touchstart',
  'touchend',
  'touchcancel',
  'click',
  'contextmenu',
] as const;

export type UseTouchDiagnosticsReturn = {
  lastEvent: TouchEventLogPayload | null;
  barRect: RectSnapshot | null;
  hudState: HudState | null;
};

const formatTouch = (t: Touch): TouchSnapshot => ({
  identifier: t.identifier,
  clientX: round(t.clientX),
  clientY: round(t.clientY),
  pageX: round(t.pageX),
  pageY: round(t.pageY),
  screenX: round(t.screenX),
  screenY: round(t.screenY),
  radiusX: round(t.radiusX),
  radiusY: round(t.radiusY),
  rotationAngle: round(t.rotationAngle),
  force: round(t.force),
});

export const useTouchDiagnostics = ({ config }: { config: DiagnosticsFlagConfig }): UseTouchDiagnosticsReturn => {
  const [lastEvent, setLastEvent] = useState<TouchEventLogPayload | null>(null);
  const [barRect, setBarRect] = useState<RectSnapshot | null>(null);
  const [hudState, setHudState] = useState<HudState | null>(null);

  const diagSessionId = useMemo(() => `${Math.random().toString(36).slice(2, 10)}-${Date.now()}`, []);
  const seqRef = useRef(1);
  const lastSnapshotTimeRef = useRef(0);
  const lastHitMapTimeRef = useRef(0);
  const seenInBubble = useRef(new WeakSet<Event>());
  // Timers for deferred offset probes, tracked so unmount can cancel any still pending.
  const pendingProbeTimers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const logger = useMemo(() => getLogger(DIAGNOSTICS_LOGGER_NAMESPACE), []);

  // Returns the seq assigned to this log entry so callers (e.g. the deferred offset probe)
  // can correlate a later message back to the event that triggered it.
  const log = useCallback(
    (eventName: string, payload: Record<string, unknown>): number => {
      const currentSeq = seqRef.current;
      seqRef.current += 1;
      try {
        const buildId =
          (typeof window !== 'undefined' &&
            (window as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__?.buildId) ??
          'unknown';

        logger.info(eventName, {
          diagSessionId,
          seq: currentSeq,
          path: typeof window !== 'undefined' ? window.location.pathname : '',
          buildId,
          ...payload,
        });
      } catch {
        // ignore
      }
      return currentSeq;
    },
    [diagSessionId, logger]
  );

  const takeEnvSnapshot = useCallback(
    (reason: string): void => {
      try {
        const now = Date.now();
        if (now - lastSnapshotTimeRef.current < 500 && reason !== 'mount' && reason !== 'routeChangeComplete') {
          return;
        }
        lastSnapshotTimeRef.current = now;
        const snapshot = collectEnvSnapshot(reason);
        log('env_snapshot', snapshot);
      } catch {
        // ignore
      }
    },
    [log]
  );

  const runHitMap = useCallback(
    (reason: string): void => {
      try {
        const now = Date.now();
        if (now - lastHitMapTimeRef.current < 2000 && reason !== 'mount' && reason !== 'routeChangeComplete') {
          return;
        }
        lastHitMapTimeRef.current = now;

        const schedule =
          typeof window !== 'undefined' &&
          (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
            ? (window as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback
            : (cb: () => void) => setTimeout(cb, 50);

        schedule(() => {
          try {
            const bar = document.querySelector<HTMLElement>(`[data-testid="${BAR_TEST_ID}"]`);
            if (bar) {
              const hitMap = computeHitMap(bar, bar.getBoundingClientRect());
              log('hit_map', { reason, ...hitMap });
            }
          } catch {
            // ignore
          }
        });
      } catch {
        // ignore
      }
    },
    [log]
  );

  useEffect(() => {
    if (!config.enabled || typeof window === 'undefined') {
      return undefined;
    }

    takeEnvSnapshot('mount');
    runHitMap('mount');

    const hitMapTimer = setInterval(() => {
      runHitMap('interval');
    }, config.hitMapIntervalMs);

    // Captured once per effect run so cleanup clears exactly this invocation's pending timers,
    // not whatever pendingProbeTimers.current happens to hold by the time cleanup fires.
    const probeTimers = pendingProbeTimers.current;

    const handleInputCapture = (e: Event): void => {
      try {
        const timeStamp = round(e.timeStamp);
        const isPointer = 'pointerType' in e;
        const isTouch = 'changedTouches' in e || 'touches' in e;
        const me = e as MouseEvent | PointerEvent;
        const te = e as TouchEvent;

        let primaryTouch: Touch | null = null;
        if (isTouch) {
          if (te.changedTouches.length > 0) {
            primaryTouch = te.changedTouches.item(0);
          } else if (te.touches.length > 0) {
            primaryTouch = te.touches.item(0);
          }
        }

        const clientX = round(primaryTouch ? primaryTouch.clientX : me.clientX);
        const clientY = round(primaryTouch ? primaryTouch.clientY : me.clientY);
        const pageX = round(primaryTouch ? primaryTouch.pageX : me.pageX);
        const pageY = round(primaryTouch ? primaryTouch.pageY : me.pageY);
        const screenX = round(primaryTouch ? primaryTouch.screenX : me.screenX);
        const screenY = round(primaryTouch ? primaryTouch.screenY : me.screenY);

        const bar = document.querySelector<HTMLElement>(`[data-testid="${BAR_TEST_ID}"]`);
        const barDomRect = bar ? bar.getBoundingClientRect() : null;
        const barRectSnapshot = bar ? getRectSnapshot(bar) : undefined;
        const targetNode = e.target instanceof Node ? e.target : null;

        let insideBarRect = false;
        let dx: number | undefined;
        let dy: number | undefined;
        let hitBar = false;
        let targetButton: string | null = null;
        // The offset probe itself is deferred (see below the input_event log call) so it never
        // adds synchronous work to the dispatch of this event; this just decides whether to schedule it.
        let probeScheduled = false;

        if (bar && barDomRect) {
          insideBarRect =
            clientX >= barDomRect.left &&
            clientX <= barDomRect.right &&
            clientY >= barDomRect.top &&
            clientY <= barDomRect.bottom;
          dx = round(clientX - barDomRect.left);
          dy = round(clientY - barDomRect.top);
          hitBar = targetNode ? bar.contains(targetNode) : false;

          if (hitBar && targetNode) {
            BUTTON_TEST_IDS.forEach((btnId) => {
              const btn = bar.querySelector(`[data-testid="${btnId}"]`);
              if (btn?.contains(targetNode)) {
                targetButton = btnId;
              }
            });
          }

          // Offset probe eligibility: pointerdown only (not touchstart, which fires alongside it for
          // the same tap on touch hardware and would otherwise double the cost), within a ±250px
          // vertical buffer of the bar.
          if (
            e.type === 'pointerdown' &&
            clientX >= barDomRect.left - 50 &&
            clientX <= barDomRect.right + 50 &&
            clientY >= barDomRect.top - 250 &&
            clientY <= barDomRect.bottom + 250
          ) {
            probeScheduled = true;
          }
        }

        const composedPath: ElementDescriptor[] = [];
        if (typeof e.composedPath === 'function') {
          const pathEls = e.composedPath();
          for (let i = 0; i < pathEls.length && i < 10; i += 1) {
            composedPath.push(describeElement(pathEls[i]));
          }
        }

        const elementsFromPoint: ElementDescriptor[] = [];
        try {
          if (typeof document.elementsFromPoint === 'function' && typeof document.elementFromPoint === 'function') {
            const efp = document.elementsFromPoint(clientX, clientY);
            if (Array.isArray(efp)) {
              for (let i = 0; i < efp.length && i < 8; i += 1) {
                elementsFromPoint.push(describeElement(efp[i]));
              }
            }
          } else if (typeof document.elementFromPoint === 'function') {
            const single = document.elementFromPoint(clientX, clientY);
            if (single) {
              elementsFromPoint.push(describeElement(single));
            }
          }
        } catch {
          // ignore
        }

        const vv = window.visualViewport;
        const visualViewportData = vv
          ? {
              width: round(vv.width),
              height: round(vv.height),
              offsetLeft: round(vv.offsetLeft),
              offsetTop: round(vv.offsetTop),
              pageLeft: round(vv.pageLeft),
              pageTop: round(vv.pageTop),
              scale: round(vv.scale),
            }
          : null;

        const payload: TouchEventLogPayload = {
          type: e.type,
          isTrusted: e.isTrusted,
          cancelable: e.cancelable,
          timeStamp,
          coords: {
            clientX,
            clientY,
            screenX,
            screenY,
            pageX,
            pageY,
          },
          target: describeElement(e.target),
          composedPath,
          elementsFromPoint,
          bar: {
            found: Boolean(bar),
            rect: barRectSnapshot,
            dx,
            dy,
            insideBarRect,
            hitBar,
            targetButton,
            probeScheduled,
          },
          scroll: {
            y: round(window.scrollY),
          },
          window: {
            innerHeight: round(window.innerHeight),
            documentElementClientHeight: round(document.documentElement.clientHeight),
          },
          visualViewport: visualViewportData,
        };

        if (isPointer) {
          const pe = e as PointerEvent;
          payload.pointer = {
            pointerType: pe.pointerType,
            width: round(pe.width),
            height: round(pe.height),
            pressure: round(pe.pressure),
          };
        }

        if (isTouch) {
          payload.touches = [];
          for (let i = 0; i < te.touches.length && i < 3; i += 1) {
            const t = te.touches.item(i);
            if (t) {
              payload.touches.push(formatTouch(t));
            }
          }
          payload.changedTouches = [];
          for (let i = 0; i < te.changedTouches.length && i < 3; i += 1) {
            const t = te.changedTouches.item(i);
            if (t) {
              payload.changedTouches.push(formatTouch(t));
            }
          }
        }

        const eventSeq = log('input_event', payload);

        // Detect if stopPropagation was called: check if event reached bubble listener
        const capturedEvent = e;
        const capturedType = e.type;
        const capturedTarget = describeElement(e.target);
        setTimeout(() => {
          try {
            if (!seenInBubble.current.has(capturedEvent)) {
              log('input_propagation_stopped', {
                type: capturedType,
                target: capturedTarget,
              });
            }
          } catch {
            // ignore
          }
        }, 0);

        // Run the actual probe (~110-130 elementFromPoint calls) off the event dispatch so it can
        // never add input latency on this low-power kiosk hardware. `eventSeq` (the seq already
        // assigned to the input_event above) is the cheap per-tap correlation key back to the tap
        // that triggered this probe.
        if (probeScheduled && bar && barDomRect) {
          const probeBar = bar;
          const probeRect = barDomRect;
          const probeTimer = setTimeout(() => {
            pendingProbeTimers.current.delete(probeTimer);
            try {
              const offsetProbe = probeBarHitRange(clientX, probeBar, probeRect);
              log('offset_probe', {
                originSeq: eventSeq,
                type: capturedType,
                coords: { clientX, clientY, pageX, pageY },
                ...offsetProbe,
              });
              if (config.overlay) {
                setHudState((prev) => (prev ? { ...prev, probeOffsetY: offsetProbe.offsetY } : prev));
              }
            } catch {
              // ignore
            }
          }, 0);
          pendingProbeTimers.current.add(probeTimer);
        }

        if (config.overlay) {
          setLastEvent(payload);
          setBarRect(barRectSnapshot ?? null);
          setHudState({
            eventType: e.type,
            clientX,
            clientY,
            pageX,
            pageY,
            insideBar: insideBarRect,
            hitBar,
            probeOffsetY: null,
          });
        }
      } catch {
        // ignore
      }
    };

    const handleInputBubble = (e: Event): void => {
      try {
        seenInBubble.current.add(e);
        if (e.defaultPrevented) {
          log('input_default_prevented', {
            type: e.type,
            defaultPrevented: true,
            target: describeElement(e.target),
          });
        }
      } catch {
        // ignore
      }
    };

    INPUT_EVENT_TYPES.forEach((type) => {
      window.addEventListener(type, handleInputCapture, { capture: true, passive: true });
      window.addEventListener(type, handleInputBubble, { capture: false, passive: true });
    });

    const handleWindowChange = (e: Event): void => {
      takeEnvSnapshot(e.type);
      runHitMap(e.type);
    };

    const windowChangeEvents = ['resize', 'orientationchange', 'fullscreenchange', 'visibilitychange'];
    windowChangeEvents.forEach((evt) => {
      window.addEventListener(evt, handleWindowChange, { passive: true });
    });

    const vv = window.visualViewport;
    const handleVvChange = (): void => {
      takeEnvSnapshot('visualViewport');
      runHitMap('visualViewport');
    };
    if (vv) {
      vv.addEventListener('resize', handleVvChange, { passive: true });
      vv.addEventListener('scroll', handleVvChange, { passive: true });
    }

    const handleRouteChange = (): void => {
      takeEnvSnapshot('routeChangeComplete');
      runHitMap('routeChangeComplete');
    };
    Router.events.on('routeChangeComplete', handleRouteChange);

    const handleError = (e: ErrorEvent): void => {
      try {
        log('window_error', {
          message: e.message ? e.message.slice(0, 200) : '',
          filename: e.filename ? e.filename.slice(0, 100) : '',
          lineno: e.lineno,
        });
      } catch {
        // ignore
      }
    };

    const handleUnhandledRejection = (e: PromiseRejectionEvent): void => {
      try {
        log('unhandled_rejection', {
          reason: String(e.reason).slice(0, 200),
        });
      } catch {
        // ignore
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    let observer: PerformanceObserver | null = null;
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            log('long_task', {
              duration: round(entry.duration),
              startTime: round(entry.startTime),
            });
          });
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch {
        observer = null;
      }
    }

    return () => {
      clearInterval(hitMapTimer);

      probeTimers.forEach((timer) => clearTimeout(timer));
      probeTimers.clear();

      INPUT_EVENT_TYPES.forEach((type) => {
        window.removeEventListener(type, handleInputCapture, { capture: true });
        window.removeEventListener(type, handleInputBubble, { capture: false });
      });

      windowChangeEvents.forEach((evt) => {
        window.removeEventListener(evt, handleWindowChange);
      });

      if (vv) {
        vv.removeEventListener('resize', handleVvChange);
        vv.removeEventListener('scroll', handleVvChange);
      }

      Router.events.off('routeChangeComplete', handleRouteChange);

      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);

      if (observer) {
        observer.disconnect();
      }
    };
  }, [config.enabled, config.hitMapIntervalMs, config.overlay, log, runHitMap, takeEnvSnapshot]);

  return {
    lastEvent,
    barRect,
    hudState,
  };
};
